import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatCurrency, formatDate, generateId } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';
import { Printer, ArrowLeft, RotateCcw, Bus } from 'lucide-react';

function FeeCollection() {
  const { feePayments, setFeePayments, feeStructures, students, classes, parents, routes, transportTerms, saveFeePayment, deleteFeePayment, schoolName } = useApp();
  const { currentUser } = useAuth();
  const [selectedClass, setSelectedClass] = useState('');
  useEffect(() => {
    if (!selectedClass && classes.length > 0) setSelectedClass(classes[0].id);
  }, [classes]); // eslint-disable-line react-hooks/exhaustive-deps
  const [ledgerStudent, setLedgerStudent] = useState(null); // student whose ledger is open
  const [collectModal, setCollectModal] = useState(false);
  const [receiptPayment, setReceiptPayment] = useState(null); // payment to show receipt for
  const [revertModal, setRevertModal] = useState(null); // payment to revert
  const [form, setForm] = useState({ amount: '', paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0, paymentType: 'current', feeLabel: '' });

  const isParent  = ['parent','father','mother'].includes(currentUser?.role);
  const isStudent = currentUser?.role === 'student';
  const parentRecord  = isParent  ? parents?.find(p => p.id === currentUser?.profileId) : null;
  const childStudents = isParent  ? students.filter(s => (s.parentId === currentUser?.profileId || s.id === currentUser?.profileId)) : [];
  const studentSelf   = isStudent ? students.find(s => s.id === currentUser?.profileId)  : null;

  const getStudentClass = (s) => { const c = classes.find(c => c.id === s.classId); return c ? `${c.name}-${c.section}` : '-'; };
  const getStudentPayments = (studentId, type = 'current') =>
    feePayments.filter(p => p.studentId === studentId && (
      type === 'current'      ? (p.type !== 'prevBalance' && p.type !== 'transportFee' && p.type !== 'admissionFee') :
      type === 'prevBalance'  ? p.type === 'prevBalance' :
      type === 'transportFee' ? p.type === 'transportFee' :
      type === 'admissionFee' ? p.type === 'admissionFee' : false
    )).sort((a, b) => a.paidDate.localeCompare(b.paidDate));

  // Returns transport route/stop/term info for a student, or null if not enrolled.
  // numTuitionTerms: when DB has no transport terms, split fee evenly across tuition terms.
  const getStudentTransport = (student, numTuitionTerms = 0) => {
    if (!student?.transportRouteId) return null;
    const route = routes.find(r => r.id === student.transportRouteId);
    if (!route) return null;
    const allStops = route.stops || [];
    // Try exact ? case-insensitive trim ? first stop with fee ? route-level fee
    let stop = allStops.find(s => s.name === student.transportStop);
    if (!stop && student.transportStop)
      stop = allStops.find(s => s.name?.toLowerCase().trim() === student.transportStop?.toLowerCase().trim());
    if (!stop)
      stop = allStops.find(s => Number(s.fee) > 0);
    const annualFee = stop ? Number(stop.fee) : Number(route.fee || 0);
    if (annualFee <= 0) return null;
    let termFees;
    if (transportTerms.length > 0) {
      termFees = transportTerms.map(t => ({ ...t, amount: Math.round(annualFee * t.percent / 100 * 100) / 100 }));
    } else if (numTuitionTerms > 1) {
      // No DB transport terms — split evenly across tuition terms so each term shows a transport row
      const perTerm = Math.round(annualFee / numTuitionTerms * 100) / 100;
      termFees = Array.from({ length: numTuitionTerms }, (_, i) => ({
        termNo: i + 1, label: `Term ${i + 1}`, percent: Math.round(100 / numTuitionTerms * 100) / 100,
        amount: perTerm, dueDate: ''
      }));
    } else {
      termFees = [{ termNo: 1, label: 'Annual', percent: 100, amount: annualFee, dueDate: '' }];
    }
    const stopName = student.transportStop || stop?.name || route.routeName || route.name || '—';
    return { route, stop, stopName, annualFee, termFees };
  };

  const findFeeStructure = (classId) => {
    const cls = classes.find(c => c.id === classId);
    const className = cls?.name;
    return [...feeStructures]
      .filter(f => (className && f.classId === className) || f.classId === classId)
      .sort((a, b) => (b.academicYear || '').localeCompare(a.academicYear || ''))[0] || null;
  };

  // Admission fee components are separate from tuition
  const admissionFeeTotal = (fs) =>
    (fs?.components || []).filter(c => c.isAdmissionFee).reduce((s, c) => s + Number(c.amount || 0), 0);

  const getStudentFeeStatus = (student) => {
    const fs = findFeeStructure(student.classId);
    if (!fs) return null;
    const admFee = admissionFeeTotal(fs);
    const prevBal = Number(student.previousYearBalance || 0);
    const currentPaid = getStudentPayments(student.id, 'current').reduce((sum, p) => sum + Number(p.amount), 0);
    const prevPaid = getStudentPayments(student.id, 'prevBalance').reduce((sum, p) => sum + Number(p.amount), 0);
    // Exclude admission fee from tuition balance
    const totalDue = Number(fs.totalAmount || 0) - admFee + prevBal;
    const totalPaid = currentPaid + prevPaid;
    const status = totalPaid >= totalDue ? 'paid' : totalPaid > 0 ? 'partial' : 'pending';
    return { fs, totalDue, paid: totalPaid, remaining: totalDue - totalPaid, status };
  };

  const getStudentAdmissionFeeStatus = (student) => {
    const fs = findFeeStructure(student.classId);
    if (!fs) return null;
    const totalDue = admissionFeeTotal(fs);
    if (totalDue <= 0) return null;
    const paid = getStudentPayments(student.id, 'admissionFee').reduce((sum, p) => sum + Number(p.amount), 0);
    const remaining = Math.max(0, totalDue - paid);
    const status = paid >= totalDue ? 'paid' : paid > 0 ? 'partial' : 'pending';
    return { totalDue, paid, remaining, status };
  };

  const getStudentTransportStatus = (student) => {
    const transport = getStudentTransport(student);
    if (!transport) return null;
    const paid = getStudentPayments(student.id, 'transportFee').reduce((sum, p) => sum + Number(p.amount), 0);
    const totalDue = transport.annualFee;
    const remaining = totalDue - paid;
    const status = paid >= totalDue ? 'paid' : paid > 0 ? 'partial' : 'pending';
    return { transport, totalDue, paid, remaining, status };
  };

  const displayStudents = isParent
    ? childStudents
    : students.filter(s => s.status === 'active' && s.classId === selectedClass)
        .sort((a, b) => Number(a.rollNumber) - Number(b.rollNumber));

  const savePayment = async () => {
    const fs = findFeeStructure(ledgerStudent.classId);
    const prevBal = ledgerStudent.previousYearBalance || 0;
    const isPrevPayment       = form.paymentType === 'prevBalance';
    const isTransportPayment  = form.paymentType === 'transportFee';
    const isAdmissionPayment  = form.paymentType === 'admissionFee';
    const admFee = admissionFeeTotal(fs);
    // Only count tuition + prevBalance payments for status check (exclude transport + admission)
    const prev = feePayments.filter(p => p.studentId === ledgerStudent.id && p.type !== 'transportFee' && p.type !== 'admissionFee').reduce((s, p) => s + Number(p.amount), 0);
    const total = prev + (isAdmissionPayment || isTransportPayment ? 0 : Number(form.amount));
    const tuitionDue = Number(fs?.totalAmount || 0) - admFee + Number(prevBal);
    const newPayment = {
      id: generateId('PAY'),
      studentId: ledgerStudent.id,
      feeStructureId: isTransportPayment ? 'TRANSPORT' : isAdmissionPayment ? 'ADMISSION' : isPrevPayment ? 'PREV' : (fs?.id || ''),
      type: isTransportPayment ? 'transportFee' : isAdmissionPayment ? 'admissionFee' : isPrevPayment ? 'prevBalance' : 'current',
      amount: Number(form.amount),
      discount: Number(form.discount) || 0,
      fine: Number(form.fine) || 0,
      paymentMode: form.paymentMode,
      transactionRef: form.transactionRef,
      paidDate: new Date().toISOString().split('T')[0],
      collectedBy: currentUser?.profileId || 'STF-001',
      feeLabel: form.feeLabel || (isTransportPayment ? 'Transport Fee' : isAdmissionPayment ? 'Admission Fee' : isPrevPayment ? 'Previous Year Balance' : 'Current Fee'),
      status: isTransportPayment ? 'partial' : isAdmissionPayment ? 'partial' : total >= tuitionDue ? 'paid' : 'partial',
    };
    const ok = await saveFeePayment(newPayment);
    if (!ok) return; // toast shown by context on failure
    setCollectModal(false);
    setForm({ amount: '', paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0, paymentType: 'current', feeLabel: '' });
    setReceiptPayment({ ...newPayment, student: ledgerStudent, fs });
  };

  const revertPayment = async (pay) => {
    const ok = await deleteFeePayment(pay.id);
    if (!ok) return;
    setRevertModal(null);
  };

  const printLedger = async (student, fs, currentPayments, prevPayments, prevBal, totalDue, totalPaid, totalBalance, feeType = 'tuition') => {
    const cls = classes.find(c => c.id === student?.classId);
    const className = cls ? `${cls.name}-${cls.section}` : '';
    const components = fs?.components || [];
    const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    const isTerm    = (c) => c.isTermFee === true || /^Tuition Fee - Term \d/.test(c.name);
    const getTermNo = (c) => c.termNo || Number(/Term (\d)/.exec(c.name)?.[1] || 0);
    const termCompsList    = components.filter(isTerm).sort((a,b)=>getTermNo(a)-getTermNo(b));
    const regularCompsList = components.filter(c => !isTerm(c));
    const hasPrintTerms    = termCompsList.length > 0;
    const currentPaid      = currentPayments.reduce((s, p) => s + Number(p.amount), 0);
    const prevPaid         = prevPayments.reduce((s, p) => s + Number(p.amount), 0);

    // Transport
    const transport         = getStudentTransport(student, termCompsList.length);
    const transportPayments = getStudentPayments(student?.id, 'transportFee');
    const transportPaid     = transportPayments.reduce((s, p) => s + Number(p.amount), 0);

    // -- Per-term metrics ------------------------------------------
    let _cum = 0;
    const termData = termCompsList.map(comp => {
      const prev = _cum; _cum += Number(comp.amount);
      const paid = Math.min(Math.max(currentPaid - prev, 0), Number(comp.amount));
      const bal  = Number(comp.amount) - paid;
      const status = currentPaid >= _cum ? 'paid' : currentPaid > prev ? 'partial' : 'pending';
      return { comp, paid, bal, status };
    });

    let _tCum = 0;
    const transData = (transport?.termFees || []).map(term => {
      const prev = _tCum; _tCum += term.amount;
      const paid = Math.min(Math.max(transportPaid - prev, 0), term.amount);
      const bal  = term.amount - paid;
      const status = transportPaid >= _tCum ? 'paid' : transportPaid > prev ? 'partial' : 'pending';
      return { ...term, paid, bal, status };
    });

    // Assign each tuition payment to the term where its cumulative-before lands
    const printPaymentsForTerm = termCompsList.map(() => []);
    { let _r = 0;
      for (const pay of currentPayments) {
        const before = _r; let _c = 0;
        for (let i = 0; i < termCompsList.length; i++) {
          _c += Number(termCompsList[i].amount);
          if (before < _c || i === termCompsList.length - 1) { printPaymentsForTerm[i].push(pay); break; }
        }
        _r += Number(pay.amount);
      }
    }

    // Assign each transport payment to the term where its cumulative-before lands
    const printTransPaymentsForTerm = transData.map(() => []);
    { let _r = 0;
      for (const pay of transportPayments) {
        const before = _r; let _c = 0;
        for (let i = 0; i < transData.length; i++) {
          _c += transData[i].amount;
          if (before < _c || i === transData.length - 1) { printTransPaymentsForTerm[i].push(pay); break; }
        }
        _r += Number(pay.amount);
      }
    }

    const TCOLORS  = ['#1d4ed8','#7c3aed','#15803d','#c2410c'];
    const HBG      = ['#eff6ff','#faf5ff','#f0fdf4','#fff7ed'];
    const HBORDER  = ['#bfdbfe','#e9d5ff','#bbf7d0','#fed7aa'];

    // Running balance tracker — counts down across ALL payment sub-rows (tuition + transport + prev)
    let runBal = totalDue;

    const paySubRows = (pays, rowStyle = '') => pays.map(pay => {
      runBal = Math.max(0, runBal - Number(pay.amount));
      const disc = Number(pay.discount || 0);
      const fine = Number(pay.fine || 0);
      const discFineNote = (disc > 0 || fine > 0)
        ? ` <span style="font-size:10px;color:#888">(disc ${fmt(disc)}, fine ${fmt(fine)})</span>` : '';
      return `<tr class="sub" style="${rowStyle}">
        <td style="padding-left:32px;color:#888"></td>
        <td></td>
        <td style="font-family:monospace;font-size:11px">${pay.id}</td>
        <td style="text-transform:capitalize">${pay.paymentMode}</td>
        <td>${formatDate(pay.paidDate)}</td>
        <td class="num" style="color:#16a34a">${fmt(pay.amount)}${discFineNote}</td>
        <td class="num" style="color:${runBal>0?'#dc2626':'#16a34a'}">${fmt(runBal)}</td>
      </tr>`;
    }).join('');

    const showTuition   = feeType === 'tuition';
    const showTransport = feeType === 'transport';
    const accentColor   = showTransport ? '#1d4ed8' : '#6b21a8';

    // Totals scoped to selected type
    const typeDue  = showTransport ? (transport?.annualFee || 0) : (totalDue - (transport?.annualFee || 0));
    const typePaid = showTransport ? transportPaid : (totalPaid - transportPaid);
    const typeBal  = Math.max(0, typeDue - typePaid);
    runBal = showTransport ? (transport?.annualFee || 0) : (totalDue - (transport?.annualFee || 0));

    let bodyRows = '';

    if (hasPrintTerms) {
      const numTerms = Math.max(termData.length, transData.length);
      for (let idx = 0; idx < numTerms; idx++) {
        const tD  = idx < termData.length  ? termData[idx]  : null;
        const trD = idx < transData.length ? transData[idx] : null;
        if (Number(tD?.comp.amount || 0) === 0 && Number(trD?.amount || 0) === 0) continue;
        const tColor  = TCOLORS[idx]  || '#374151';
        const hBg     = HBG[idx]      || '#f9fafb';
        const hBorder = HBORDER[idx]  || '#e5e7eb';
        const dueDate = tD?.comp.termDueDate || trD?.dueDate || '';

        if (showTuition && tD) {
          bodyRows += `<tr style="background:${hBg};border-top:2px solid ${hBorder}">
            <td colspan="7" style="padding:7px 10px">
              <span style="background:${tColor}22;color:${tColor};padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;margin-right:8px">Term ${idx+1}</span>
              <strong style="color:#374151">Tuition Fee — Term ${idx+1}</strong>
              ${dueDate ? `<span style="color:#9ca3af;font-size:11px;margin-left:8px">Due: ${formatDate(dueDate)}</span>` : ''}
            </td>
          </tr>`;
          const statusColor = tD.status === 'paid' ? '#16a34a' : tD.status === 'partial' ? '#b45309' : '#dc2626';
          bodyRows += `<tr><td style="padding-left:20px;font-weight:600">Tuition Fee</td>
            <td class="num">${fmt(tD.comp.amount)}</td><td></td><td></td><td></td>
            <td class="num" style="color:#16a34a">${tD.paid > 0 ? fmt(tD.paid) : '—'}</td>
            <td class="num" style="color:${statusColor}">${fmt(tD.bal)}</td></tr>`;
          if (printPaymentsForTerm[idx]?.length > 0) bodyRows += paySubRows(printPaymentsForTerm[idx]);
        }

        if (showTransport && trD) {
          bodyRows += `<tr style="background:#eff6ff;border-top:2px solid #bfdbfe">
            <td colspan="7" style="padding:7px 10px">
              <span style="background:#1d4ed822;color:#1d4ed8;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;margin-right:8px">Term ${idx+1}</span>
              <strong style="color:#1e40af">&#128652; Transport Fee — Term ${idx+1}</strong>
              ${dueDate ? `<span style="color:#9ca3af;font-size:11px;margin-left:8px">Due: ${formatDate(dueDate)}</span>` : ''}
            </td>
          </tr>`;
          const statusColor = trD.status === 'paid' ? '#16a34a' : trD.status === 'partial' ? '#b45309' : '#dc2626';
          bodyRows += `<tr style="background:#eff6ff40"><td style="padding-left:20px;font-weight:600;color:#1e40af">Transport Fee</td>
            <td class="num">${fmt(trD.amount)}</td><td></td><td></td><td></td>
            <td class="num" style="color:#16a34a">${trD.paid > 0 ? fmt(trD.paid) : '—'}</td>
            <td class="num" style="color:${statusColor}">${fmt(trD.bal)}</td></tr>`;
          if (printTransPaymentsForTerm[idx]?.length > 0) bodyRows += paySubRows(printTransPaymentsForTerm[idx], 'background:#eff6ff');
        }
      }
    } else {
      if (showTuition) {
        const totalTuitionBal = (fs?.totalAmount || 0) - currentPaid;
        regularCompsList.forEach((comp, idx) => {
          const isFirst = idx === 0;
          bodyRows += `<tr class="head">
            <td>${comp.name}</td><td class="num">${fmt(comp.amount)}</td><td></td><td></td><td></td>
            <td class="num" style="color:#16a34a">${isFirst && currentPaid > 0 ? fmt(currentPaid) : '—'}</td>
            <td class="num" style="color:${totalTuitionBal>0?'#dc2626':'#16a34a'}">${isFirst ? fmt(Math.max(0, totalTuitionBal)) : '—'}</td>
          </tr>`;
          if (isFirst) bodyRows += paySubRows(currentPayments);
        });
      }

      if (showTransport && transport) {
        bodyRows += `<tr style="background:#eff6ff;border-top:2px solid #bfdbfe">
          <td colspan="7" style="padding:7px 10px;color:#1e40af;font-weight:600;font-size:11px">
            &#128652; School Transport — ${transport.route.routeName || transport.route.name} / ${transport.stopName}
            <span style="color:#60a5fa;font-weight:400"> (${fmt(transport.annualFee)}/year)</span>
          </td>
        </tr>`;
        transData.forEach((term, idx) => {
          const statusColor = term.status === 'paid' ? '#16a34a' : term.status === 'partial' ? '#b45309' : '#dc2626';
          bodyRows += `<tr class="head" style="background:#eff6ff40">
            <td><span style="background:#dbeafe;color:#1d4ed8;padding:1px 6px;border-radius:10px;font-size:10px;font-weight:600;margin-right:6px">T${idx+1}</span>&#128652; ${term.label} — Transport</td>
            <td class="num">${fmt(term.amount)}</td><td></td><td></td><td></td>
            <td class="num" style="color:#16a34a">${term.paid > 0 ? fmt(term.paid) : '—'}</td>
            <td class="num" style="color:${statusColor}">${fmt(term.bal)}</td>
          </tr>`;
          if (printTransPaymentsForTerm[idx]?.length > 0) bodyRows += paySubRows(printTransPaymentsForTerm[idx], 'background:#eff6ff');
        });
      }
    }

    if (showTuition && prevBal > 0) {
      bodyRows += `<tr class="head prev-head">
        <td style="color:#c2410c;font-weight:600">Previous Year Balance</td>
        <td class="num" style="color:#c2410c">${fmt(prevBal)}</td><td></td><td></td><td></td>
        <td class="num" style="color:#16a34a">${prevPaid > 0 ? fmt(prevPaid) : '—'}</td>
        <td class="num" style="color:${prevBal-prevPaid>0?'#dc2626':'#16a34a'}">${fmt(Math.max(0, prevBal-prevPaid))}</td>
      </tr>`;
      if (prevPayments.length > 0) bodyRows += paySubRows(prevPayments, 'background:#fff7ed');
    }

    const school = await getSchoolInfo();
    const docTitle = showTransport ? 'Transport Fee Ledger' : 'Tuition Fee Ledger';
    const win = window.open('', '_blank', 'width=860,height=700');
    win.document.open(); win.document.write(`
      <html><head><title>${docTitle} - ${student?.firstName} ${student?.lastName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Roboto',Arial,sans-serif; padding:28px 32px; color:#111; font-size:15px; }
        .school-header { margin-bottom:16px; }
        .meta { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid #e5e7eb; }
        .meta-left .name { font-size:16px; font-weight:700; color:${accentColor}; }
        .meta-left .details { font-size:12px; color:#555; margin-top:3px; }
        .meta-right { text-align:right; font-size:12px; color:#555; }
        .meta-right .total { font-size:14px; font-weight:700; color:#111; }
        table { width:100%; border-collapse:collapse; }
        thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:12px; padding:8px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
        thead th.num { text-align:right; }
        td { padding:7px 10px; border-bottom:1px solid #f3f4f6; vertical-align:middle; font-size:12px; }
        tr.head td { font-weight:600; background:#fff; }
        tr.sub td { color:#111; background:#fafafa; }
        tr.prev-head td { background:#fff7ed; }
        tr.total td { background:#f1f5f9; font-weight:700; border-top:2px solid #d1d5db; border-bottom:none; font-size:13px; }
        .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .footer { text-align:center; font-size:11px; color:#aaa; margin-top:24px; padding-top:8px; border-top:1px dashed #e5e7eb; }
        .print-btn { text-align:center; margin-top:18px; }
        @page { margin:3mm; } @media print { .print-btn { display:none; } body { padding:4px; } * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; } img { display:block!important; } }
      </style></head><body>
      ${schoolHeader(school, accentColor, `${docTitle} &mdash; Academic Year ${fs?.academicYear || '2025-2026'}`)}
      <div class="meta">
        <div class="meta-left">
          <div class="name">${student?.firstName} ${student?.lastName}</div>
          <div class="details">Class: ${className} &nbsp;&bull;&nbsp; Roll No: ${student?.rollNumber} &nbsp;&bull;&nbsp; Adm No: ${student?.admissionNumber || '—'}</div>
        </div>
        <div class="meta-right">
          <div class="total">Total Due: ${fmt(typeDue)}</div>
          <div>Paid: ${fmt(typePaid)} &nbsp;&bull;&nbsp; Balance: <span style="color:${typeBal>0?'#dc2626':'#16a34a'}">${fmt(typeBal)}</span></div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>Fees</th><th class="num">Amount</th><th>Payment ID</th>
          <th>Mode</th><th>Date</th><th class="num">Paid (&#8377;)</th><th class="num">Balance (&#8377;)</th>
        </tr></thead>
        <tbody>
          ${bodyRows}
          <tr class="total">
            <td>Grand Total</td>
            <td class="num">${fmt(typeDue)}</td>
            <td colspan="3"></td>
            <td class="num" style="color:#16a34a">${fmt(typePaid)}</td>
            <td class="num" style="color:${typeBal>0?'#dc2626':'#16a34a'}">${fmt(typeBal)}</td>
          </tr>
        </tbody>
      </table>
      <div class="footer">Computer-generated document &mdash; ${schoolName}</div>
      <div class="print-btn">
        <button onclick="window.print()" style="background:#6b21a8;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer">&#128424; Print</button>
      </div>
      </body></html>
    `);
    win.document.close();
  };

  const printReceipt = async (pay) => {
    const student = pay.student || ledgerStudent;
    const fs = pay.fs || findFeeStructure(student?.classId);
    const cls = classes.find(c => c.id === student?.classId);
    const className = cls ? `${cls.name}-${cls.section}` : '';
    const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    // All payments for this student (tuition, transport, prev balance)
    const allCurrentPays = getStudentPayments(student?.id, 'current');
    const allPrevPays    = getStudentPayments(student?.id, 'prevBalance');
    const allTransPays   = getStudentPayments(student?.id, 'transportFee');
    const prevBal        = Number(student?.previousYearBalance || 0);
    const transport      = getStudentTransport(student);
    const transportAnnual = transport?.annualFee || 0;

    // Tuition totals (current + prev balance)
    const tuitionDue    = Number(fs?.totalAmount || 0) + prevBal;
    const tuitionPaid   = [...allCurrentPays, ...allPrevPays].reduce((s, p) => s + Number(p.amount), 0);
    const tuitionBal    = tuitionDue - tuitionPaid;

    // Transport totals
    const transPaid     = allTransPays.reduce((s, p) => s + Number(p.amount), 0);
    const transBal      = transportAnnual - transPaid;

    // Grand totals
    const totalDue      = tuitionDue + transportAnnual;
    const totalPaid     = tuitionPaid + transPaid;
    const totalBalance  = totalDue - totalPaid;

    const getFeeLabel = (p) => p.feeLabel || (
      p.type === 'prevBalance'  ? 'Previous Year Balance' :
      p.type === 'transportFee' ? 'Transport Fee' :
      p.type === 'admissionFee' ? 'Admission Fee' : 'Current Fee'
    );

    // Tuition payment rows (current + prev), running balance against tuitionDue
    const tuitionRows = [...allCurrentPays, ...allPrevPays]
      .sort((a, b) => a.paidDate.localeCompare(b.paidDate))
      .map((p, i, arr) => {
        const paidSoFar = arr.slice(0, i + 1).reduce((s, x) => s + Number(x.amount), 0);
        const bal = tuitionDue - paidSoFar;
        const isCurrent = p.id === pay.id;
        return `<tr style="${isCurrent ? 'background:#fffbeb;' : ''}">
          <td>${formatDate(p.paidDate)}</td><td class="mono">${p.id}</td>
          <td>${getFeeLabel(p)}</td>
          <td style="text-transform:capitalize">${p.paymentMode}</td>
          <td>${p.transactionRef || '—'}</td>
          <td class="num" style="color:#16a34a">${fmt(p.amount)}</td>
          <td class="num" style="color:${bal > 0 ? '#dc2626' : '#16a34a'}">${fmt(Math.max(0, bal))}</td>
          <td>${isCurrent ? '<span class="badge-current">Current</span>' : ''}</td>
        </tr>`;
      }).join('');

    // Transport payment rows, running balance against transportAnnual
    const transRows = allTransPays
      .sort((a, b) => a.paidDate.localeCompare(b.paidDate))
      .map((p, i, arr) => {
        const paidSoFar = arr.slice(0, i + 1).reduce((s, x) => s + Number(x.amount), 0);
        const bal = transportAnnual - paidSoFar;
        const isCurrent = p.id === pay.id;
        return `<tr style="${isCurrent ? 'background:#eff6ff;' : ''}">
          <td>${formatDate(p.paidDate)}</td><td class="mono">${p.id}</td>
          <td>${getFeeLabel(p)}</td>
          <td style="text-transform:capitalize">${p.paymentMode}</td>
          <td>${p.transactionRef || '—'}</td>
          <td class="num" style="color:#16a34a">${fmt(p.amount)}</td>
          <td class="num" style="color:${bal > 0 ? '#dc2626' : '#16a34a'}">${fmt(Math.max(0, bal))}</td>
          <td>${isCurrent ? '<span class="badge-current">Current</span>' : ''}</td>
        </tr>`;
      }).join('');

    const isTuitionPay   = pay.type !== 'transportFee' && pay.type !== 'admissionFee';
    const isTransportPay = pay.type === 'transportFee';

    const studentMeta = (receiptNo) => `
      <div class="meta">
        <div class="meta-left">
          <div class="name">${student?.firstName} ${student?.lastName}</div>
          <div class="details">Class: ${className} &nbsp;&bull;&nbsp; Roll No: ${student?.rollNumber} &nbsp;&bull;&nbsp; Adm No: ${student?.admissionNumber || '—'}</div>
        </div>
        <div class="meta-right">
          <div class="receipt-no">Receipt No: ${receiptNo}</div>
          <div>Date: ${formatDate(pay.paidDate)}</div>
          <div>Mode: <strong style="text-transform:capitalize">${pay.paymentMode}</strong></div>
        </div>
      </div>`;

    const tableHead = `<thead><tr>
      <th>Date</th><th>Receipt No</th><th>Description</th>
      <th>Mode</th><th>Reference</th>
      <th class="num">Amount (&#8377;)</th><th class="num">Balance (&#8377;)</th><th></th>
    </tr></thead>`;

    const school = await getSchoolInfo();
    const win = window.open('', '_blank', 'width=900,height=750');
    win.document.open(); win.document.write(`
      <html><head><title>Fee Receipt - ${student?.firstName} ${student?.lastName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing:border-box; margin:0; padding:0; }
        /* Screen: gray background, each page looks like a paper sheet */
        body { font-family:'Roboto',Arial,sans-serif; color:#111; font-size:15px; background:#d1d5db; }
        .pages-wrapper { display:flex; flex-direction:column; align-items:center; padding:24px 16px; gap:24px; }
        .page { background:#fff; width:794px; min-height:1000px; padding:32px 36px; box-shadow:0 4px 16px rgba(0,0,0,.18); border-radius:2px; position:relative; }
        /* Print: each .page starts on a new physical page, no shadow/bg */
        @page { margin: 3mm; }
        @media print {
          body { background:#fff; }
          .pages-wrapper { padding:0; gap:0; display:block; }
          .page { box-shadow:none; border-radius:0; width:100%; min-height:0; padding:4px 6px; break-after:page; page-break-after:always; }
          .page:last-child { break-after:avoid; page-break-after:avoid; }
          .print-btn { display:none; }
          * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; }
          img { display:block!important; }
        }
        .school-header { margin-bottom:16px; }
        .school-header-row { display:flex; align-items:center; justify-content:space-between; padding-bottom:10px; }
        .school-header.tuition   .school-header-row { border-bottom:2px solid #6b21a8; }
        .school-header.transport .school-header-row { border-bottom:2px solid #1d4ed8; }
        .school-header p { font-size:12px; color:#555; margin-top:3px; }
        .meta { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid #e5e7eb; }
        .meta-left .name { font-size:16px; font-weight:700; color:#374151; }
        .meta-left .details { font-size:12px; color:#555; margin-top:3px; }
        .meta-right { text-align:right; font-size:12px; color:#555; }
        .meta-right .receipt-no { font-size:14px; font-weight:700; color:#111; }
        .summary { display:flex; gap:0; margin-bottom:18px; border-radius:8px; border:1px solid #e5e7eb; overflow:hidden; }
        .summary-item { flex:1; padding:12px 14px; border-right:1px solid #e5e7eb; }
        .summary-item:last-child { border-right:none; }
        .summary-item .lbl { font-size:10px; color:#888; text-transform:uppercase; letter-spacing:.4px; margin-bottom:3px; }
        .summary-item .val { font-size:15px; font-weight:700; color:#111; }
        .summary.tuition   { background:#faf5ff; }
        .summary.transport { background:#eff6ff; }
        .section-title { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; margin:0 0 8px; padding:6px 10px; border-radius:4px; }
        .section-title.tuition   { background:#f3e8ff; color:#6b21a8; }
        .section-title.transport { background:#dbeafe; color:#1d4ed8; }
        table { width:100%; border-collapse:collapse; }
        thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:12px; padding:8px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
        thead th.num { text-align:right; }
        td { padding:7px 10px; border-bottom:1px solid #f3f4f6; vertical-align:middle; font-size:12px; }
        tr.total-row td { font-weight:700; border-top:2px solid #d1d5db; border-bottom:none; font-size:13px; }
        tr.total-row.tuition   td { background:#f3e8ff; }
        tr.total-row.transport td { background:#dbeafe; }
        .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .mono { font-family:monospace; font-size:11px; }
        .badge-current { display:inline-block; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .badge-current.tuition   { background:#6b21a8; color:#fff; }
        .badge-current.transport { background:#1d4ed8; color:#fff; }
        .footer { text-align:center; font-size:11px; color:#aaa; margin-top:20px; padding-top:8px; border-top:1px dashed #e5e7eb; }
        .print-btn { text-align:center; padding:20px; }
      </style></head><body>
      <div class="pages-wrapper">

        <!-- -- PAGE 1 : TUITION FEE -- -->
        ${!isTransportPay ? `<div class="page">
          <div class="school-header tuition">
            ${schoolHeader(school, '#6b21a8', `Tuition Fee Receipt &mdash; ${fs?.academicYear || '2025-2026'}`)}
          </div>
          ${studentMeta(pay.id)}
          <div class="summary tuition">
            <div class="summary-item">
              <div class="lbl">Tuition Fee Due</div>
              <div class="val">${fmt(tuitionDue)}</div>
            </div>
            <div class="summary-item">
              <div class="lbl">Total Paid</div>
              <div class="val" style="color:#16a34a">${fmt(tuitionPaid)}</div>
            </div>
            <div class="summary-item">
              <div class="lbl">Balance</div>
              <div class="val" style="color:${tuitionBal > 0 ? '#dc2626' : '#16a34a'}">${fmt(Math.max(0, tuitionBal))}</div>
            </div>
            ${isTuitionPay ? `<div class="summary-item" style="background:#fffbeb">
              <div class="lbl">This Payment</div>
              <div class="val" style="color:#6b21a8">${fmt(pay.amount)}</div>
            </div>` : ''}
          </div>
          <div class="section-title tuition">Tuition Payment History</div>
          <table>
            ${tableHead}
            <tbody>
              ${tuitionRows || `<tr><td colspan="8" style="text-align:center;color:#aaa;padding:16px">No tuition payments recorded</td></tr>`}
            </tbody>
            <tfoot>
              <tr class="total-row tuition">
                <td colspan="5">Tuition Total</td>
                <td class="num" style="color:#16a34a">${fmt(tuitionPaid)}</td>
                <td class="num" style="color:${tuitionBal > 0 ? '#dc2626' : '#16a34a'}">${fmt(Math.max(0, tuitionBal))}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <div class="footer">Computer-generated receipt &mdash; ${schoolName}</div>
        </div>` : ''}

        ${isTransportPay && transport ? `
        <!-- -- PAGE 2 : TRANSPORT FEE -- -->
        <div class="page">
          <div class="school-header transport">
            ${schoolHeader(school, '#1d4ed8', `Transport Fee Receipt &mdash; ${fs?.academicYear || '2025-2026'}`)}
          </div>
          ${studentMeta(pay.id)}
          <div class="summary transport">
            <div class="summary-item">
              <div class="lbl">Transport Fee Due</div>
              <div class="val">${fmt(transportAnnual)}</div>
            </div>
            <div class="summary-item">
              <div class="lbl">Total Paid</div>
              <div class="val" style="color:#16a34a">${fmt(transPaid)}</div>
            </div>
            <div class="summary-item">
              <div class="lbl">Balance</div>
              <div class="val" style="color:${transBal > 0 ? '#dc2626' : '#16a34a'}">${fmt(Math.max(0, transBal))}</div>
            </div>
            ${isTransportPay ? `<div class="summary-item" style="background:#eff6ff">
              <div class="lbl">This Payment</div>
              <div class="val" style="color:#1d4ed8">${fmt(pay.amount)}</div>
            </div>` : ''}
          </div>
          <div class="section-title transport">Transport Payment History</div>
          <table>
            ${tableHead}
            <tbody>
              ${transRows || `<tr><td colspan="8" style="text-align:center;color:#aaa;padding:16px">No transport payments recorded</td></tr>`}
            </tbody>
            <tfoot>
              <tr class="total-row transport">
                <td colspan="5">Transport Total</td>
                <td class="num" style="color:#16a34a">${fmt(transPaid)}</td>
                <td class="num" style="color:${transBal > 0 ? '#dc2626' : '#16a34a'}">${fmt(Math.max(0, transBal))}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <div class="footer">Computer-generated receipt &mdash; ${schoolName}</div>
        </div>` : ''}

        <div class="print-btn">
          <button onclick="window.print()" style="background:${isTransportPay ? '#1d4ed8' : '#6b21a8'};color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer;font-family:'Roboto',Arial,sans-serif">&#128424; Print</button>
        </div>

      </div>
      </body></html>
    `);
    win.document.close();
  };

  // -- Parent view --------------------------------------------------------------
  if (isParent) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Fee Receipts</h1>
          <p className="text-gray-500 text-sm">Your child's fee details and payment history</p>
        </div>
        {childStudents.map(student => {
          const feeStatus = getStudentFeeStatus(student);
          const transportStatus = getStudentTransportStatus(student);
          const tuitionReceipts  = [...getStudentPayments(student.id, 'current'), ...getStudentPayments(student.id, 'prevBalance')]
            .sort((a, b) => a.paidDate.localeCompare(b.paidDate));
          const transportReceipts = getStudentPayments(student.id, 'transportFee');

          const payTable = (rows, accentBg, accentText) => (
            <div className="bg-white rounded-b-xl border border-t-0 overflow-hidden" style={{ borderColor: accentBg === 'bg-purple-50' ? '#e9d5ff' : '#bfdbfe' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className={`${accentBg} border-b`} style={{ borderColor: accentBg === 'bg-purple-50' ? '#e9d5ff' : '#bfdbfe' }}>
                    <th className={`px-4 py-2 text-left font-bold ${accentText}`}>Receipt No</th>
                    <th className={`px-4 py-2 text-left font-bold ${accentText}`}>Date</th>
                    <th className={`px-4 py-2 text-left font-bold ${accentText}`}>Description</th>
                    <th className={`px-4 py-2 text-left font-bold ${accentText}`}>Mode</th>
                    <th className={`px-4 py-2 text-left font-bold ${accentText}`}>Reference</th>
                    <th className={`px-4 py-2 text-right font-bold ${accentText}`}>Amount</th>
                    <th className={`px-4 py-2 text-center font-bold ${accentText}`}>Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-5 text-center text-gray-400 text-sm">No payments recorded yet</td></tr>
                  ) : rows.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs">{r.id}</td>
                      <td className="px-4 py-2">{formatDate(r.paidDate)}</td>
                      <td className="px-4 py-2 text-gray-600">{r.feeLabel || (r.type === 'transportFee' ? 'Transport Fee' : r.type === 'prevBalance' ? 'Previous Balance' : r.type === 'admissionFee' ? 'Admission Fee' : 'Tuition Fee')}</td>
                      <td className="px-4 py-2 capitalize">{r.paymentMode}</td>
                      <td className="px-4 py-2 text-gray-500">{r.transactionRef || '—'}</td>
                      <td className="px-4 py-2 text-right font-semibold text-green-600">{formatCurrency(r.amount)}</td>
                      <td className="px-4 py-2 text-center">
                        <button onClick={() => printReceipt({ ...r, student, fs: findFeeStructure(student.classId) })}
                          className="text-gray-400 hover:text-gray-700" title="Print receipt"><Printer size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

          return (
            <div key={student.id} className="mb-8">
              {/* Student header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-lg">{student.firstName} {student.lastName}</h2>
                  <p className="text-sm text-gray-500">{getStudentClass(student)} &middot; Roll No: {student.rollNumber}</p>
                </div>
              </div>

              {/* -- Tuition Fee -- */}
              {feeStatus ? (
                <div className="mb-5">
                  <div className="flex items-center justify-between px-4 py-3 bg-purple-50 border border-purple-200 rounded-t-xl">
                    <div className="flex items-center gap-4 flex-wrap">
                      <h3 className="font-bold text-purple-800 text-sm">Tuition Fee</h3>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${feeStatus.remaining <= 0 ? 'bg-green-100 text-green-700' : feeStatus.paid > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                        {feeStatus.remaining <= 0 ? 'Paid' : feeStatus.paid > 0 ? 'Partial' : 'Pending'}
                      </span>
                      <div className="flex gap-4 text-xs text-gray-600">
                        <span>Due: <strong>{formatCurrency(feeStatus.totalDue)}</strong></span>
                        <span>Paid: <strong className="text-green-600">{formatCurrency(feeStatus.paid)}</strong></span>
                        <span>Balance: <strong style={{ color: feeStatus.remaining > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, feeStatus.remaining))}</strong></span>
                      </div>
                    </div>
                  </div>
                  {payTable(tuitionReceipts, 'bg-purple-50', 'text-purple-800')}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-400 mb-5">No fee structure defined for this class.</div>
              )}

              {/* -- Transport Fee -- */}
              {transportStatus && (
                <div className="mb-5">
                  <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-200 rounded-t-xl">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <Bus size={14} className="text-blue-700" />
                        <h3 className="font-bold text-blue-800 text-sm">Transport Fee</h3>
                      </div>
                      <span className="text-xs text-gray-500">{transportStatus.transport.route.routeName || transportStatus.transport.route.name} / {transportStatus.transport.stopName}</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${transportStatus.remaining <= 0 ? 'bg-green-100 text-green-700' : transportStatus.paid > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                        {transportStatus.remaining <= 0 ? 'Paid' : transportStatus.paid > 0 ? 'Partial' : 'Pending'}
                      </span>
                      <div className="flex gap-4 text-xs text-gray-600">
                        <span>Due: <strong>{formatCurrency(transportStatus.totalDue)}</strong></span>
                        <span>Paid: <strong className="text-green-600">{formatCurrency(transportStatus.paid)}</strong></span>
                        <span>Balance: <strong style={{ color: transportStatus.remaining > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, transportStatus.remaining))}</strong></span>
                      </div>
                    </div>
                  </div>
                  {payTable(transportReceipts, 'bg-blue-50', 'text-blue-800')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // -- Student self-view ---------------------------------------------------------
  if (isStudent) {
    const student = studentSelf;
    const feeStatus = student ? getStudentFeeStatus(student) : null;
    const receipts = student ? getStudentPayments(student.id) : [];
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">My Fee Details</h1>
          <p className="text-gray-500 text-sm">Your fee summary and payment history</p>
        </div>
        {!student ? (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">Student record not found.</div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-lg">{student.firstName} {student.lastName}</h2>
                  <p className="text-sm text-gray-500">{getStudentClass(student)} &middot; Roll No: {student.rollNumber}</p>
                </div>
                {feeStatus && <Badge value={feeStatus.status} />}
              </div>
              {feeStatus ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-500 mb-1">Total Fee</p>
                    <p className="text-lg font-bold">{formatCurrency(feeStatus.totalDue)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-500 mb-1">Paid</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(feeStatus.paid)}</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${feeStatus.remaining > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <p className="text-sm text-gray-500 mb-1">Balance Due</p>
                    <p className={`text-lg font-bold ${feeStatus.remaining > 0 ? 'text-red-500' : 'text-green-600'}`}>{formatCurrency(feeStatus.remaining)}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No fee structure defined for your class.</p>
              )}
            </div>
            <h3 className="text-sm font-semibold mb-2 px-1">Payment History</h3>
            {receipts.length === 0 ? (
              <div className="bg-white rounded-xl p-6 text-center text-sm border border-gray-100 text-gray-500">No payments recorded yet</div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-2 text-left font-bold">Receipt No</th>
                      <th className="px-4 py-2 text-left font-bold">Date</th>
                      <th className="px-4 py-2 text-left font-bold">Mode</th>
                      <th className="px-4 py-2 text-left font-bold">Reference</th>
                      <th className="px-4 py-2 text-right font-bold">Amount</th>
                      <th className="px-4 py-2 text-center font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {receipts.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono">{r.id}</td>
                        <td className="px-4 py-2">{formatDate(r.paidDate)}</td>
                        <td className="px-4 py-2 capitalize">{r.paymentMode}</td>
                        <td className="px-4 py-2">{r.transactionRef || '—'}</td>
                        <td className="px-4 py-2 text-right font-semibold text-green-600">{formatCurrency(r.amount)}</td>
                        <td className="px-4 py-2 text-center"><Badge value={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // -- Student Fee Ledger (after clicking Collect) -------------------------------
  if (ledgerStudent) {
    const fs = findFeeStructure(ledgerStudent.classId);
    const prevBal = Number(ledgerStudent.previousYearBalance || 0);

    const currentPayments = getStudentPayments(ledgerStudent.id, 'current');
    const prevPayments    = getStudentPayments(ledgerStudent.id, 'prevBalance');
    const transportPayments = getStudentPayments(ledgerStudent.id, 'transportFee');

    const currentPaid    = currentPayments.reduce((s, p) => s + Number(p.amount), 0);
    const prevPaid       = prevPayments.reduce((s, p) => s + Number(p.amount), 0);
    const transportPaidTotal = transportPayments.reduce((s, p) => s + Number(p.amount), 0);
    const totalDiscount  = [...currentPayments, ...prevPayments].reduce((s, p) => s + Number(p.discount || 0), 0);
    const totalFine      = [...currentPayments, ...prevPayments].reduce((s, p) => s + Number(p.fine || 0), 0);
    const _admFeeForLedger = admissionFeeTotal(fs);
    const tuitionDue     = Number(fs?.totalAmount || 0) - _admFeeForLedger + prevBal;
    const tuitionPaid    = currentPaid + prevPaid;
    const tuitionBalance = tuitionDue - tuitionPaid;

    // Fee components — must come before transport so we know termComps.length
    const components = fs?.components || [];
    const isTerm = (c) => c.isTermFee === true || /^Tuition Fee - Term \d/.test(c.name);
    const getTermNo = (c) => c.termNo || Number(/Term (\d)/.exec(c.name)?.[1] || 0);
    const termComps = components.filter(isTerm).sort((a, b) => getTermNo(a) - getTermNo(b));
    const regularComps = components.filter(c => !isTerm(c));
    const hasTermFees = termComps.length > 0;

    // Admission Fee
    const admFeeTotal = admissionFeeTotal(fs);
    const admissionPayments = getStudentPayments(ledgerStudent.id, 'admissionFee');
    const admissionPaid = admissionPayments.reduce((s, p) => s + Number(p.amount), 0);
    const admissionBalance = Math.max(0, admFeeTotal - admissionPaid);
    const admissionStatus = admFeeTotal > 0 ? (admissionPaid >= admFeeTotal ? 'paid' : admissionPaid > 0 ? 'partial' : 'pending') : null;
    const admissionComps = (fs?.components || []).filter(c => c.isAdmissionFee);

    // Transport
    const transport = getStudentTransport(ledgerStudent, termComps.length);
    const transportTotalDue = transport?.annualFee || 0;
    const transportBalance  = transportTotalDue - transportPaidTotal;
    let _tCum = 0;
    const transportTermMetrics = (transport?.termFees || []).map(term => {
      const prevCum = _tCum;
      _tCum += term.amount;
      const termPaid = Math.min(Math.max(transportPaidTotal - prevCum, 0), term.amount);
      const termBal  = term.amount - termPaid;
      const termStatus = transportPaidTotal >= _tCum ? 'paid' : transportPaidTotal > prevCum ? 'partial' : 'pending';
      return { termPaid, termBal, termStatus };
    });

    const totalDue     = tuitionDue + transportTotalDue + admFeeTotal;
    const totalPaid    = tuitionPaid + transportPaidTotal + admissionPaid;
    const totalBalance = tuitionBalance + transportBalance;
    const overallStatus  = totalPaid >= totalDue ? 'paid' : totalPaid > 0 ? 'partial' : 'pending';

    // Pre-compute per-term paid/balance outside JSX to avoid mutable-closure issues
    let _cum = 0;
    const termMetrics = termComps.map(comp => {
      const prevThreshold = _cum;
      _cum += Number(comp.amount);
      const termPaid = Math.min(Math.max(currentPaid - prevThreshold, 0), Number(comp.amount));
      const termBal  = Number(comp.amount) - termPaid;
      const termStatus = currentPaid >= _cum ? 'paid' : currentPaid > prevThreshold ? 'partial' : 'pending';
      return { termPaid, termBal, termStatus };
    });
    const activeTermIdx = termMetrics.findIndex(m => m.termStatus !== 'paid');

    // Assign each payment to the term where its cumulative-paid-before lands
    // e.g. if Term1=?14k and payments are ?5k,?5k,?5k,?1k:
    //   payments 1-3 (cumBefore 0,5k,10k) all land in Term1 ? shown under Term1
    //   payment  4  (cumBefore 15k)        lands in Term2  ? shown under Term2
    const paymentsForTerm = termComps.map(() => []);
    let _runPaid = 0;
    for (const pay of currentPayments) {
      const before = _runPaid;
      let _c = 0;
      for (let i = 0; i < termComps.length; i++) {
        _c += Number(termComps[i].amount);
        if (before < _c || i === termComps.length - 1) { paymentsForTerm[i].push(pay); break; }
      }
      _runPaid += Number(pay.amount);
    }

    // Assign each transport payment to its term
    const transportPaymentsForTerm = (transport?.termFees || []).map(() => []);
    { let _tr = 0;
      for (const pay of transportPayments) {
        const before = _tr; let _c = 0;
        const tFees = transport?.termFees || [];
        for (let i = 0; i < tFees.length; i++) {
          _c += tFees[i].amount;
          if (before < _c || i === tFees.length - 1) { transportPaymentsForTerm[i].push(pay); break; }
        }
        _tr += Number(pay.amount);
      }
    }

    const openCollect = (amount, paymentType = 'current', feeLabel = '') => {
      setForm({ amount: String(amount), paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0, paymentType, feeLabel });
      setCollectModal(true);
    };

    const renderPaySubRows = (pays, termAmount) => {
      let _termPaid = 0;
      return pays.map((pay) => {
        _termPaid += Number(pay.amount);
        const termBal = Math.max(0, termAmount - _termPaid);
        return (
          <tr key={pay.id} className="border-b border-gray-50 bg-gray-50/50">
            <td className="px-3 py-1"></td>
            <td className="px-3 py-1 pl-8"></td>
            <td className="px-3 py-1">—</td>
            <td className="px-3 py-1 text-center">—</td>
            <td className="px-3 py-1 text-right">—</td>
            <td className="px-3 py-1 max-w-[120px] truncate" title={pay.id}>{pay.id}</td>
            <td className="px-3 py-1 capitalize">{pay.paymentMode}</td>
            <td className="px-3 py-1">{formatDate(pay.paidDate)}</td>
            <td className="px-3 py-1 text-right" style={{ color: (pay.discount || 0) > 0 ? '#2563eb' : '#111827' }}>{formatCurrency(pay.discount || 0)}</td>
            <td className="px-3 py-1 text-right">{formatCurrency(pay.fine || 0)}</td>
            <td className="px-3 py-1 text-right font-medium">{formatCurrency(pay.amount)}</td>
            <td className="px-3 py-1 text-right font-medium" style={{ color: termBal > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(termBal)}</td>
            <td className="px-3 py-1 text-center">
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => printReceipt({ ...pay, student: ledgerStudent, fs })} className="text-gray-400 hover:text-gray-700" title="Print receipt"><Printer size={13} /></button>
                <button onClick={() => setRevertModal(pay)} className="text-red-400 hover:text-red-600" title="Revert payment"><RotateCcw size={13} /></button>
              </div>
            </td>
          </tr>
        );
      });
    };

    return (
      <div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setLedgerStudent(null)} className="flex items-center gap-1.5 text-sm hover:text-gray-800">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="h-4 w-px bg-gray-300" />
          <div>
            <h1 className="text-xl font-bold">{ledgerStudent.firstName} {ledgerStudent.lastName}</h1>
            <p className="">{getStudentClass(ledgerStudent)} &middot; Roll No: {ledgerStudent.rollNumber}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full font-semibold ${overallStatus === 'paid' ? 'bg-green-100 text-green-700' : overallStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
              {overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
            </span>
            <button onClick={() => printLedger(ledgerStudent, fs, currentPayments, prevPayments, prevBal, totalDue, totalPaid, totalBalance, 'tuition')}
              className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50">
              <Printer size={14} /> Tuition Ledger
            </button>
            {getStudentTransport(ledgerStudent) && (
              <button onClick={() => printLedger(ledgerStudent, fs, currentPayments, prevPayments, prevBal, totalDue, totalPaid, totalBalance, 'transport')}
                className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50">
                <Printer size={14} /> Transport Ledger
              </button>
            )}
            {overallStatus !== 'paid' && (
              <button onClick={() => { setForm({ amount: String(totalBalance), paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0, paymentType: 'current', feeLabel: 'Current Fee' }); setCollectModal(true); }}
                className="px-4 py-1 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: '#6b21a8' }}>
                + Collect Payment
              </button>
            )}
          </div>
        </div>

        {/* -- Section 1: Tuition Fee -- */}
        <div className="mb-6">
          <div className="flex items-center justify-between px-4 py-3 bg-purple-50 border border-purple-200 rounded-t-xl">
            <div className="flex items-center gap-4 flex-wrap">
              <h2 className="font-bold text-purple-800">Tuition Fee</h2>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${tuitionBalance <= 0 ? 'bg-green-100 text-green-700' : tuitionPaid > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                {tuitionBalance <= 0 ? 'Paid' : tuitionPaid > 0 ? 'Partial' : 'Pending'}
              </span>
              <div className="flex gap-4 text-xs text-gray-600">
                <span>Due: <strong>{formatCurrency(tuitionDue)}</strong></span>
                <span>Paid: <strong className="text-green-600">{formatCurrency(tuitionPaid)}</strong></span>
                <span>Balance: <strong style={{ color: tuitionBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, tuitionBalance))}</strong></span>
              </div>
            </div>
            {tuitionBalance > 0 && (
              <button onClick={() => openCollect(tuitionBalance, 'current', 'Current Fee')}
                className="px-3 py-1 text-sm font-medium text-white rounded-lg shrink-0" style={{ backgroundColor: '#6b21a8' }}>
                + Collect
              </button>
            )}
          </div>
          <div className="bg-white border border-t-0 border-purple-200 rounded-b-xl overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="px-3 py-1 w-6"></th>
                  <th className="px-3 py-1 text-left font-bold">Fee Component</th>
                  <th className="px-3 py-1 text-left font-bold">Due Date</th>
                  <th className="px-3 py-1 text-center font-bold">Status</th>
                  <th className="px-3 py-1 text-right font-bold">Amount (₹)</th>
                  <th className="px-3 py-1 text-left font-bold">Payment ID</th>
                  <th className="px-3 py-1 text-left font-bold">Mode</th>
                  <th className="px-3 py-1 text-left font-bold">Date</th>
                  <th className="px-3 py-1 text-right font-bold">Discount (₹)</th>
                  <th className="px-3 py-1 text-right font-bold">Fine (₹)</th>
                  <th className="px-3 py-1 text-right font-bold">Paid (₹)</th>
                  <th className="px-3 py-1 text-right font-bold">Balance (₹)</th>
                  <th className="px-3 py-1 text-center font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
              {/* -- Tuition: term-based layout -- */}
              {hasTermFees ? (() => {
                const HEADING_BG  = ['bg-blue-50 border-blue-200','bg-purple-50 border-purple-200','bg-green-100 border-green-300'];
                const CHIP_COLORS = ['bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-green-100 text-green-700'];
                return termComps.map((tComp, idx) => {
                  if (Number(tComp.amount) === 0) return null;
                  const tMet = termMetrics[idx];
                  const hBg  = HEADING_BG[idx]  || 'bg-gray-50 border-gray-300';
                  const chip = CHIP_COLORS[idx] || 'bg-gray-100 text-gray-700';
                  return (
                    <React.Fragment key={`tg-${idx}`}>
                      <tr className={`border-t-2 border-b ${hBg}`}>
                        <td className="px-3 py-1.5" colSpan={13}>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${chip}`}>Term {idx + 1}</span>
                            {tComp.termDueDate && <span className="text-xs text-gray-400 ml-1">Due: {formatDate(tComp.termDueDate)}</span>}
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-1"><input type="checkbox" className="rounded" /></td>
                        <td className="px-3 py-1 pl-8 font-semibold text-gray-800">Tuition Fee</td>
                        <td className="px-3 py-1">{tComp.termDueDate ? formatDate(tComp.termDueDate) : '—'}</td>
                        <td className="px-3 py-1 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${tMet.termStatus === 'paid' ? 'bg-green-500 text-white' : tMet.termStatus === 'partial' ? 'bg-yellow-400 text-white' : 'bg-gray-200 text-gray-600'}`}>
                            {tMet.termStatus.charAt(0).toUpperCase() + tMet.termStatus.slice(1)}
                          </span>
                        </td>
                        <td className="px-3 py-1 text-right font-medium">{formatCurrency(tComp.amount)}</td>
                        <td className="px-3 py-1">—</td><td className="px-3 py-1">—</td><td className="px-3 py-1">—</td>
                        <td className="px-3 py-1 text-right">{paymentsForTerm[idx]?.length > 0 ? formatCurrency(paymentsForTerm[idx].reduce((s, p) => s + Number(p.discount || 0), 0)) : '—'}</td>
                        <td className="px-3 py-1 text-right">{paymentsForTerm[idx]?.length > 0 ? formatCurrency(paymentsForTerm[idx].reduce((s, p) => s + Number(p.fine || 0), 0)) : '—'}</td>
                        <td className="px-3 py-1 text-right">—</td>
                        <td className="px-3 py-1 text-right">—</td>
                        <td className="px-3 py-1 text-center">
                          {tMet.termBal > 0 ? (
                            <button onClick={() => openCollect(tMet.termBal, 'current', tComp.name)}
                              className="text-xs font-semibold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#6b21a8' }}>
                              Collect
                            </button>
                          ) : <span className="text-green-600 text-xs font-semibold">? Paid</span>}
                        </td>
                      </tr>
                      {paymentsForTerm[idx]?.length > 0 && renderPaySubRows(paymentsForTerm[idx], Number(tComp.amount))}
                    </React.Fragment>
                  );
                });
              })() : (
                regularComps.map((comp, idx) => {
                  const isFirst = idx === 0;
                  const compBalance = isFirst ? (fs?.totalAmount || 0) - currentPaid : 0;
                  const compStatus = isFirst ? (currentPaid >= (fs?.totalAmount || 0) ? 'paid' : currentPaid > 0 ? 'partial' : 'pending') : 'pending';
                  return (
                    <React.Fragment key={`reg-${idx}`}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-1"><input type="checkbox" className="rounded" /></td>
                        <td className="px-3 py-1 font-semibold">{comp.name}</td>
                        <td className="px-3 py-1">{fs?.dueDate ? formatDate(fs.dueDate) : '—'}</td>
                        <td className="px-3 py-1 text-center">
                          <span className={`px-2 py-0.5 rounded font-semibold text-xs ${compStatus === 'paid' ? 'bg-green-500 text-white' : compStatus === 'partial' ? 'bg-yellow-400 text-white' : 'bg-gray-200'}`}>
                            {compStatus.charAt(0).toUpperCase() + compStatus.slice(1)}
                          </span>
                        </td>
                        <td className="px-3 py-1 text-right font-medium">{formatCurrency(comp.amount)}</td>
                        <td className="px-3 py-1">—</td><td className="px-3 py-1">—</td><td className="px-3 py-1">—</td>
                        <td className="px-3 py-1 text-right">{isFirst ? formatCurrency(currentPayments.reduce((s, p) => s + (p.discount || 0), 0)) : '—'}</td>
                        <td className="px-3 py-1 text-right">{isFirst ? formatCurrency(currentPayments.reduce((s, p) => s + (p.fine || 0), 0)) : '—'}</td>
                        <td className="px-3 py-1 text-right">—</td><td className="px-3 py-1 text-right">—</td>
                        <td className="px-3 py-1 text-center">
                          {isFirst && compBalance > 0 ? (
                            <button onClick={() => openCollect(compBalance, 'current', comp.name)}
                              className="text-xs font-semibold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#6b21a8' }}>
                              Collect
                            </button>
                          ) : isFirst ? <span className="text-green-600 text-xs font-semibold">? Paid</span> : null}
                        </td>
                      </tr>
                      {isFirst && renderPaySubRows(currentPayments, fs?.totalAmount || 0)}
                    </React.Fragment>
                  );
                })
              )}

              {/* Previous Year Balance */}
              {prevBal > 0 && (
                <React.Fragment>
                  <tr className="border-b border-gray-100 hover:bg-orange-50/30 bg-orange-50/20">
                    <td className="px-3 py-1"><input type="checkbox" className="rounded" /></td>
                    <td className="px-3 py-1 font-semibold text-orange-700">Previous Year Balance</td>
                    <td className="px-3 py-1">Carried forward</td>
                    <td className="px-3 py-1 text-center">
                      <span className={`px-2 py-0.5 rounded font-semibold text-xs ${prevPaid >= prevBal ? 'bg-green-500 text-white' : prevPaid > 0 ? 'bg-yellow-400 text-white' : 'bg-red-100 text-red-600'}`}>
                        {prevPaid >= prevBal ? 'Paid' : prevPaid > 0 ? 'Partial' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-3 py-1 text-right text-orange-700 font-medium">{formatCurrency(prevBal)}</td>
                    <td className="px-3 py-1">—</td><td className="px-3 py-1">—</td><td className="px-3 py-1">—</td>
                    <td className="px-3 py-1 text-right">—</td><td className="px-3 py-1 text-right">—</td>
                    <td className="px-3 py-1 text-right">—</td>
                    <td className="px-3 py-1 text-right font-medium" style={{ color: prevBal - prevPaid > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, prevBal - prevPaid))}</td>
                    <td className="px-3 py-1 text-center">
                      {prevBal - prevPaid > 0 ? (
                        <button onClick={() => openCollect(prevBal - prevPaid, 'prevBalance', 'Previous Year Balance')}
                          className="text-xs font-semibold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#c2410c' }}>
                          Collect
                        </button>
                      ) : <span className="text-green-600 text-xs font-semibold">? Paid</span>}
                    </td>
                  </tr>
                  {renderPaySubRows(prevPayments, prevBal)}
                </React.Fragment>
              )}

              {/* Tuition Subtotal */}
              <tr className="bg-purple-50 font-semibold border-t-2 border-purple-200 text-sm">
                <td className="px-3 py-1.5" colSpan={4}>Tuition Total</td>
                <td className="px-3 py-1.5 text-right">{formatCurrency(tuitionDue)}</td>
                <td colSpan={5}></td>
                <td className="px-3 py-1.5 text-right text-green-700">{formatCurrency(tuitionPaid)}</td>
                <td className="px-3 py-1.5 text-right" style={{ color: tuitionBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, tuitionBalance))}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>

        {/* -- Section 2: Transport Fee -- */}
        {transport && (
          <div className="mb-6">
            <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-200 rounded-t-xl">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Bus size={15} className="text-blue-700" />
                  <h2 className="font-bold text-blue-800">Transport Fee</h2>
                </div>
                <span className="text-xs text-gray-500">{transport.route.routeName || transport.route.name} / {transport.stopName}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${transportBalance <= 0 ? 'bg-green-100 text-green-700' : transportPaidTotal > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                  {transportBalance <= 0 ? 'Paid' : transportPaidTotal > 0 ? 'Partial' : 'Pending'}
                </span>
                <div className="flex gap-4 text-xs text-gray-600">
                  <span>Due: <strong>{formatCurrency(transportTotalDue)}</strong></span>
                  <span>Paid: <strong className="text-green-600">{formatCurrency(transportPaidTotal)}</strong></span>
                  <span>Balance: <strong style={{ color: transportBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, transportBalance))}</strong></span>
                </div>
              </div>
              {transportBalance > 0 && (
                <button onClick={() => openCollect(transportBalance, 'transportFee', 'Transport Fee')}
                  className="px-3 py-1 text-sm font-medium text-white rounded-lg shrink-0" style={{ backgroundColor: '#2563eb' }}>
                  + Collect
                </button>
              )}
            </div>
            <div className="bg-white border border-t-0 border-blue-200 rounded-b-xl overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-blue-50/60 border-b border-blue-100">
                    <th className="px-3 py-1 w-6"></th>
                    <th className="px-3 py-1 text-left font-bold">Term</th>
                    <th className="px-3 py-1 text-left font-bold">Due Date</th>
                    <th className="px-3 py-1 text-center font-bold">Status</th>
                    <th className="px-3 py-1 text-right font-bold">Amount (₹)</th>
                    <th className="px-3 py-1 text-left font-bold">Payment ID</th>
                    <th className="px-3 py-1 text-left font-bold">Mode</th>
                    <th className="px-3 py-1 text-left font-bold">Date</th>
                    <th className="px-3 py-1 text-right font-bold">Paid (₹)</th>
                    <th className="px-3 py-1 text-right font-bold">Balance (₹)</th>
                    <th className="px-3 py-1 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transport.termFees.map((term, idx) => {
                    if (Number(term.amount) === 0) return null;
                    const { termBal, termStatus } = transportTermMetrics[idx];
                    const CHIPS = ['bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-green-100 text-green-700'];
                    const chip  = CHIPS[idx] || 'bg-gray-100 text-gray-700';
                    return (
                      <React.Fragment key={`trans-${idx}`}>
                        <tr className="border-b border-blue-50 hover:bg-blue-50/40">
                          <td className="px-3 py-1"><input type="checkbox" className="rounded" /></td>
                          <td className="px-3 py-1 font-semibold">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium mr-1.5 ${chip}`}>T{idx+1}</span>
                            <Bus size={11} className="inline text-blue-500 mr-1" />{term.label}
                          </td>
                          <td className="px-3 py-1">{term.dueDate ? formatDate(term.dueDate) : '—'}</td>
                          <td className="px-3 py-1 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${termStatus === 'paid' ? 'bg-green-500 text-white' : termStatus === 'partial' ? 'bg-yellow-400 text-white' : 'bg-gray-200'}`}>
                              {termStatus.charAt(0).toUpperCase() + termStatus.slice(1)}
                            </span>
                          </td>
                          <td className="px-3 py-1 text-right font-medium">{formatCurrency(term.amount)}</td>
                          <td className="px-3 py-1">—</td><td className="px-3 py-1">—</td><td className="px-3 py-1">—</td>
                          <td className="px-3 py-1 text-right">—</td>
                          <td className="px-3 py-1 text-right">—</td>
                          <td className="px-3 py-1 text-center">
                            {termBal > 0 ? (
                              <button onClick={() => openCollect(termBal, 'transportFee', `${term.label} — Transport`)}
                                className="text-xs font-semibold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#2563eb' }}>
                                Collect
                              </button>
                            ) : <span className="text-green-600 text-xs font-semibold">? Paid</span>}
                          </td>
                        </tr>
                        {transportPaymentsForTerm[idx]?.length > 0 && (() => {
                          let _tp = 0;
                          return transportPaymentsForTerm[idx].map(pay => {
                            _tp += Number(pay.amount);
                            const tBal = Math.max(0, term.amount - _tp);
                            return (
                              <tr key={pay.id} className="border-b border-blue-50 bg-blue-50/30">
                                <td className="px-3 py-1" />
                                <td className="px-3 py-1 pl-8 text-gray-400"></td>
                                <td className="px-3 py-1">—</td><td className="px-3 py-1 text-center">—</td>
                                <td className="px-3 py-1 text-right">—</td>
                                <td className="px-3 py-1 max-w-[120px] truncate" title={pay.id}>{pay.id}</td>
                                <td className="px-3 py-1 capitalize">{pay.paymentMode}</td>
                                <td className="px-3 py-1">{formatDate(pay.paidDate)}</td>
                                <td className="px-3 py-1 text-right font-medium">{formatCurrency(pay.amount)}</td>
                                <td className="px-3 py-1 text-right font-medium" style={{ color: tBal > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(tBal)}</td>
                                <td className="px-3 py-1 text-center">
                                  <div className="flex gap-2 justify-center">
                                    <button onClick={() => printReceipt({ ...pay, student: ledgerStudent, fs })} className="text-gray-400 hover:text-gray-700"><Printer size={13} /></button>
                                    <button onClick={() => setRevertModal(pay)} className="text-red-400 hover:text-red-600"><RotateCcw size={13} /></button>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </React.Fragment>
                    );
                  })}
                  {/* Transport Subtotal */}
                  <tr className="bg-blue-50 font-semibold border-t-2 border-blue-200 text-sm">
                    <td className="px-3 py-1.5" colSpan={4}>Transport Total</td>
                    <td className="px-3 py-1.5 text-right">{formatCurrency(transportTotalDue)}</td>
                    <td colSpan={3}></td>
                    <td className="px-3 py-1.5 text-right text-green-700">{formatCurrency(transportPaidTotal)}</td>
                    <td className="px-3 py-1.5 text-right" style={{ color: transportBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(Math.max(0, transportBalance))}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -- Section 3: Admission Fee -- */}
        {admFeeTotal > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between px-4 py-3 bg-orange-50 border border-orange-200 rounded-t-xl">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-orange-600 font-bold text-base">★</span>
                  <h2 className="font-bold text-orange-800">Admission Fee</h2>
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${admissionBalance <= 0 ? 'bg-green-100 text-green-700' : admissionPaid > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                  {admissionBalance <= 0 ? 'Paid' : admissionPaid > 0 ? 'Partial' : 'Pending'}
                </span>
                <div className="flex gap-4 text-xs text-gray-600">
                  <span>Due: <strong>{formatCurrency(admFeeTotal)}</strong></span>
                  <span>Paid: <strong className="text-green-600">{formatCurrency(admissionPaid)}</strong></span>
                  <span>Balance: <strong style={{ color: admissionBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(admissionBalance)}</strong></span>
                </div>
              </div>
              {admissionBalance > 0 && (
                <button onClick={() => openCollect(admissionBalance, 'admissionFee', 'Admission Fee')}
                  className="px-3 py-1 text-sm font-medium text-white rounded-lg shrink-0" style={{ backgroundColor: '#ea580c' }}>
                  + Collect
                </button>
              )}
            </div>
            <div className="bg-white border border-t-0 border-orange-200 rounded-b-xl overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-orange-50/60 border-b border-orange-100">
                    <th className="px-3 py-1 w-6"></th>
                    <th className="px-3 py-1 text-left font-bold">Component</th>
                    <th className="px-3 py-1 text-left font-bold">Due Date</th>
                    <th className="px-3 py-1 text-center font-bold">Status</th>
                    <th className="px-3 py-1 text-right font-bold">Amount (₹)</th>
                    <th className="px-3 py-1 text-left font-bold">Payment ID</th>
                    <th className="px-3 py-1 text-left font-bold">Mode</th>
                    <th className="px-3 py-1 text-left font-bold">Date</th>
                    <th className="px-3 py-1 text-right font-bold">Paid (₹)</th>
                    <th className="px-3 py-1 text-right font-bold">Balance (₹)</th>
                    <th className="px-3 py-1 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admissionComps.map((comp, idx) => {
                    const compDue = Number(comp.amount || 0);
                    const compPaid = idx === 0 ? admissionPaid : 0;
                    const compBal = idx === 0 ? admissionBalance : 0;
                    const compStatus = idx === 0 ? admissionStatus : 'pending';
                    return (
                      <React.Fragment key={`adm-comp-${idx}`}>
                        <tr className="border-b border-orange-50 hover:bg-orange-50/30">
                          <td className="px-3 py-1"><input type="checkbox" className="rounded" /></td>
                          <td className="px-3 py-1 font-semibold text-orange-800">{comp.name}</td>
                          <td className="px-3 py-1">{fs?.dueDate ? formatDate(fs.dueDate) : '—'}</td>
                          <td className="px-3 py-1 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${compStatus === 'paid' ? 'bg-green-500 text-white' : compStatus === 'partial' ? 'bg-yellow-400 text-white' : 'bg-gray-200 text-gray-600'}`}>
                              {compStatus ? compStatus.charAt(0).toUpperCase() + compStatus.slice(1) : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-1 text-right font-medium">{formatCurrency(compDue)}</td>
                          <td className="px-3 py-1">—</td><td className="px-3 py-1">—</td><td className="px-3 py-1">—</td>
                          <td className="px-3 py-1 text-right">—</td>
                          <td className="px-3 py-1 text-right">—</td>
                          <td className="px-3 py-1 text-center">
                            {idx === 0 && compBal > 0 ? (
                              <button onClick={() => openCollect(compBal, 'admissionFee', comp.name)}
                                className="text-xs font-semibold text-white px-2 py-0.5 rounded" style={{ backgroundColor: '#ea580c' }}>
                                Collect
                              </button>
                            ) : idx === 0 ? <span className="text-green-600 text-xs font-semibold">✓ Paid</span> : null}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  {(() => { let _ap = 0; return admissionPayments.map(pay => {
                    _ap += Number(pay.amount);
                    const aBal = Math.max(0, admFeeTotal - _ap);
                    return (
                      <tr key={pay.id} className="border-b border-orange-50 bg-orange-50/20">
                        <td className="px-3 py-1" />
                        <td className="px-3 py-1 pl-8 text-gray-400"></td>
                        <td className="px-3 py-1">—</td><td className="px-3 py-1 text-center">—</td>
                        <td className="px-3 py-1 text-right">—</td>
                        <td className="px-3 py-1 max-w-[120px] truncate" title={pay.id}>{pay.id}</td>
                        <td className="px-3 py-1 capitalize">{pay.paymentMode}</td>
                        <td className="px-3 py-1">{formatDate(pay.paidDate)}</td>
                        <td className="px-3 py-1 text-right font-medium">{formatCurrency(pay.amount)}</td>
                        <td className="px-3 py-1 text-right font-medium" style={{ color: aBal > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(aBal)}</td>
                        <td className="px-3 py-1 text-center">
                          <div className="flex gap-2 justify-center">
                            <button onClick={() => printReceipt({ ...pay, student: ledgerStudent, fs })} className="text-gray-400 hover:text-gray-700"><Printer size={13} /></button>
                            <button onClick={() => setRevertModal(pay)} className="text-red-400 hover:text-red-600"><RotateCcw size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  }); })()}
                  {/* Admission Subtotal */}
                  <tr className="bg-orange-50 font-semibold border-t-2 border-orange-200 text-sm">
                    <td className="px-3 py-1.5" colSpan={4}>Admission Total</td>
                    <td className="px-3 py-1.5 text-right">{formatCurrency(admFeeTotal)}</td>
                    <td colSpan={3}></td>
                    <td className="px-3 py-1.5 text-right text-green-700">{formatCurrency(admissionPaid)}</td>
                    <td className="px-3 py-1.5 text-right" style={{ color: admissionBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(admissionBalance)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Collect Payment Modal */}
        {/* Receipt success modal */}
        <Modal isOpen={!!receiptPayment} onClose={() => setReceiptPayment(null)} title="Payment Collected">
          {receiptPayment && (
            <div className="text-center py-2">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <p className="text-lg font-bold mb-1">₹{Number(receiptPayment.amount).toLocaleString('en-IN')} Collected</p>
              <p className="text-sm mb-1">{receiptPayment.student?.firstName} {receiptPayment.student?.lastName}</p>
              <p className="mb-4">Receipt No: {receiptPayment.id} &middot; {formatDate(receiptPayment.paidDate)} &middot; <span className="capitalize">{receiptPayment.paymentMode}</span></p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setReceiptPayment(null)} className="px-4 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
                <button onClick={() => { printReceipt(receiptPayment); setReceiptPayment(null); }}
                  className="flex items-center gap-2 px-4 py-1 text-sm text-white rounded-lg" style={{ backgroundColor: '#6b21a8' }}>
                  <Printer size={14} /> Print Receipt
                </button>
              </div>
            </div>
          )}
        </Modal>

        <Modal isOpen={collectModal} onClose={() => setCollectModal(false)} title={form.paymentType === 'prevBalance' ? 'Collect — Previous Year Balance' : form.paymentType === 'admissionFee' ? 'Collect — Admission Fee' : form.paymentType === 'transportFee' ? 'Collect — Transport Fee' : 'Collect Payment'}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-medium mb-1">Amount (₹)</label>
                <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block font-medium mb-1">Payment Mode</label>
                <select value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                  {['cash', 'cheque', 'online', 'UPI', 'DD'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block font-medium mb-1">Transaction Reference / Cheque No.</label>
              <input value={form.transactionRef} onChange={e => setForm(f => ({ ...f, transactionRef: e.target.value }))}
                className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Optional" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-medium mb-1">Discount (₹)</label>
                <input type="number" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block font-medium mb-1">Fine (₹)</label>
                <input type="number" value={form.fine} onChange={e => setForm(f => ({ ...f, fine: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <button onClick={() => setCollectModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={savePayment} disabled={!form.amount}
              className="px-4 py-1 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#16a34a' }}>
              Collect Payment
            </button>
          </div>
        </Modal>

        {/* Revert payment confirmation */}
        <Modal isOpen={!!revertModal} onClose={() => setRevertModal(null)} title="Revert Payment" size="sm">
          {revertModal && (
            <div>
              <p className="text-gray-700 mb-2">Are you sure you want to revert this payment?</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Payment ID</span><span className="font-mono font-medium">{revertModal.id}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-semibold text-red-600">{formatCurrency(revertModal.amount)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Mode</span><span className="capitalize">{revertModal.paymentMode}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date</span><span>{formatDate(revertModal.paidDate)}</span></div>
                {revertModal.feeLabel && <div className="flex justify-between"><span className="text-gray-500">Fee Type</span><span>{revertModal.feeLabel}</span></div>}
              </div>
              <p className="text-xs text-gray-400 mb-4">This action cannot be undone. The payment record will be permanently removed and the student's balance will be updated.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setRevertModal(null)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={() => revertPayment(revertModal)}
                  className="px-4 py-1.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg">
                  Revert Payment
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  }

  // -- Admin student list view ---------------------------------------------------
  const selectedClassObj = classes.find(c => c.id === selectedClass);

  const classSelector = (() => {
    const classNames = [...new Set(classes.map(c => c.name))].sort((a, b) => {
      const nA = parseInt(a.replace(/\D/g, '')) || 0;
      const nB = parseInt(b.replace(/\D/g, '')) || 0;
      return nA !== nB ? nA - nB : a.localeCompare(b);
    });
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 mb-5">
        <div className="flex items-start gap-6 flex-wrap">
          <label className="text-sm font-semibold mt-1 shrink-0">Select Class</label>
          <div className="flex flex-wrap gap-3 flex-1">
            {classNames.map(name => {
              const sections = classes.filter(c => c.name === name).sort((a, b) => a.section.localeCompare(b.section));
              const isGroupActive = sections.some(s => s.id === selectedClass);
              return (
                <div key={name} className={`rounded-lg border overflow-hidden ${isGroupActive ? 'border-purple-300' : 'border-gray-200'}`}>
                  <div className={`px-3 py-1 text-xs font-semibold border-b ${isGroupActive ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                    {name}
                  </div>
                  <div className="flex">
                    {sections.map(s => (
                      <button key={s.id} onClick={() => setSelectedClass(s.id)}
                        className={`px-3 py-1 text-sm font-medium transition-colors ${selectedClass === s.id ? 'text-white' : 'hover:bg-gray-50 text-gray-600'}`}
                        style={selectedClass === s.id ? { backgroundColor: '#6b21a8' } : {}}>
                        {s.section}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {selectedClassObj && (
            <span className="bg-gray-100 px-2.5 py-1 rounded-lg text-sm font-medium shrink-0">{displayStudents.length} students</span>
          )}
        </div>
      </div>
    );
  })();

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Fee Collection</h1>
        <p className="text-gray-500 text-sm">Select a class to view and collect student fees</p>
      </div>

      {classSelector}

      {displayStudents.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">No active students in this class</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-2 text-left font-bold">Roll</th>
                  <th className="px-4 py-2 text-left font-bold">Student</th>
                  <th className="px-4 py-2 text-left font-bold">Parent Phone</th>
                  <th className="px-4 py-2 text-right font-bold">Total Fee</th>
                  <th className="px-4 py-2 text-right font-bold">Paid</th>
                  <th className="px-4 py-2 text-right font-bold">Balance</th>
                  <th className="px-4 py-2 text-center font-bold">Status</th>
                  <th className="px-4 py-2 text-center font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayStudents.map(student => {
                  const feeStatus = getStudentFeeStatus(student);
                  const parentPhone = parents.find(p => p.id === student.parentId)?.phone || '—';
                  if (!feeStatus) return (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{student.rollNumber}</td>
                      <td className="px-4 py-2 font-medium">{student.firstName} {student.lastName}</td>
                      <td className="px-4 py-2 text-gray-400">{parentPhone}</td>
                      <td colSpan={5} className="px-4 py-2 text-center text-gray-400 text-xs">No fee structure defined</td>
                    </tr>
                  );
                  return (
                    <tr key={student.id} className={`hover:bg-gray-50 cursor-pointer ${feeStatus.status === 'paid' ? 'bg-green-50/30' : ''}`}
                      onClick={() => setLedgerStudent(student)}>
                      <td className="px-4 py-2">{student.rollNumber}</td>
                      <td className="px-4 py-2 font-medium">{student.firstName} {student.lastName}</td>
                      <td className="px-4 py-2 text-gray-500">{parentPhone}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(feeStatus.totalDue)}</td>
                      <td className="px-4 py-2 text-right text-green-600 font-medium">{formatCurrency(feeStatus.paid)}</td>
                      <td className="px-4 py-2 text-right font-medium" style={{ color: feeStatus.remaining > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(feeStatus.remaining)}</td>
                      <td className="px-4 py-2 text-center"><Badge value={feeStatus.status} /></td>
                      <td className="px-4 py-2 text-center">
                        <button onClick={e => { e.stopPropagation(); setLedgerStudent(student); }}
                          className="font-medium text-white px-3 py-1 rounded-lg text-xs"
                          style={{ backgroundColor: feeStatus.status === 'paid' ? '#6b7280' : '#6b21a8' }}>
                          {feeStatus.status === 'paid' ? 'View' : 'Collect'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      )}
    </div>
  );
}
export default FeeCollection;
